import { record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { Ord } from 'fp-ts/string';
import { OnboardingAuditArea, OnboardingAuditField, OnboardingAuditResult } from '@mews-commander/data-access';
import { groupAudits } from './auditTransforms';

/** Creates the selector for react-query that groups the audits ready for use in the grouped tables  */
export const createAuditSelect = ({
    legalEnvironmentData,
    kycStatus,
    ...auditChecks
}: OnboardingAuditResult): {
    auditList: OnboardingAuditField[];
    groupedAudits: OnboardingAuditField[];
} => {
    const groupedAudits = pipe(
        auditChecks,
        record.filter((audit: OnboardingAuditField) => audit.area !== OnboardingAuditArea.Marketplace),
        groupAudits
    );

    const auditList = pipe(
        auditChecks,
        record.filter((audit: OnboardingAuditField) => audit.area !== OnboardingAuditArea.Marketplace),
        record.collect(Ord)((_key, audit) => audit)
    );

    return { auditList, groupedAudits };
};

const financialAndSpacesOrder = [
    OnboardingAuditArea.PropertySettings,
    OnboardingAuditArea.FinancialSettings,
    OnboardingAuditArea.Users,
    OnboardingAuditArea.Spaces,
];

const isFinancialAndSpaces = (area: OnboardingAuditArea) => financialAndSpacesOrder.includes(area);
const compareFinancialAndSpacesOrder = (a: OnboardingAuditArea, b: OnboardingAuditArea) =>
    financialAndSpacesOrder.indexOf(a) - financialAndSpacesOrder.indexOf(b);
export const getFinancialAndSpaces = (audits: OnboardingAuditField[]) =>
    audits
        .filter((audit) => isFinancialAndSpaces(audit.area))
        .sort((a, b) => compareFinancialAndSpacesOrder(a.area, b.area));

const isServiceOrProduct = (area: OnboardingAuditArea) =>
    area === OnboardingAuditArea.AdditionalServices || area === OnboardingAuditArea.BookableServices;
export const getServicesAndProducts = (audits: OnboardingAuditField[]) =>
    audits.filter((audit) => isServiceOrProduct(audit.area));

const isRevenueManagement = (area: OnboardingAuditArea) =>
    area === OnboardingAuditArea.Rates || area === OnboardingAuditArea.Companies;
export const getRevenueManagement = (audits: OnboardingAuditField[]) =>
    audits.filter((audit) => isRevenueManagement(audit.area));

const isGuestExperience = (area: OnboardingAuditArea) =>
    area === OnboardingAuditArea.GuestExperience || area === OnboardingAuditArea.Tasks;
export const getGuestExperience = (audits: OnboardingAuditField[]) =>
    audits.filter((audit) => isGuestExperience(audit.area));
